import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Save, Globe } from "lucide-react";
import { toast } from "sonner";

export default function AdminCriteria() {
  const [c, setC] = useState(null);
  const [pub, setPub] = useState(null);
  useEffect(() => {
    api.get("/admin/criteria").then((r) => setC(r.data));
    api.get("/admin/criteria-public").then((r) => setPub(r.data)).catch(() => {});
  }, []);
  if (!c) return <div className="p-8" style={{ color: "#475569" }}>Loading…</div>;
  const setKey = (k, v) => setC({ ...c, [k]: v });
  const save = async () => {
    try {
      await api.put("/admin/criteria", { ...c, min_age: Number(c.min_age) });
      toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
  };
  const setPubText = (i, text) => {
    const next = pub.criteria.map((item, idx) => (idx === i ? { ...item, text } : item));
    setPub({ ...pub, criteria: next });
  };
  const savePub = async () => {
    try {
      const { data } = await api.put("/admin/criteria-public", { criteria: pub.criteria });
      setPub(data);
      toast.success(`Published criteria saved as version ${data.version}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl" style={{ color: "#0B2D59" }}>Objective criteria</h1>
      <p className="text-sm" style={{ color: "#475569" }}>
        Establish the written rental criteria before reviewing applications. Apply these standards to every applicant.
      </p>
      <div className="p2p-card p-6 mt-5 space-y-3 text-sm">
        <Row label="Minimum age">
          <input type="number" className="p2p-input" value={c.min_age} onChange={(e) => setKey("min_age", e.target.value)} />
        </Row>
        {[
          ["application_must_be_complete", "Application must be complete"],
          ["income_verifiable", "Income or other lawful resources can be verified"],
          ["references_contactable", "Required references can be contacted"],
          ["lease_acknowledged", "Applicant agrees to lease requirements"],
          ["occupancy_compliant", "Intended occupancy complies with lawful occupancy limits"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-3">
            <input type="checkbox" checked={!!c[k]} onChange={(e) => setKey(k, e.target.checked)} />
            <span style={{ color: "#0B2D59" }}>{label}</span>
          </label>
        ))}
        <Row label="Affordability standard (text)">
          <textarea rows={2} className="p2p-input" value={c.affordability_note} onChange={(e) => setKey("affordability_note", e.target.value)} />
        </Row>
        <Row label="Additional notes">
          <textarea rows={2} className="p2p-input" value={c.additional_notes || ""} onChange={(e) => setKey("additional_notes", e.target.value)} />
        </Row>
      </div>
      <button className="p2p-btn-primary mt-5 inline-flex items-center gap-2" onClick={save}>
        <Save className="w-4 h-4" /> Save criteria
      </button>

      {pub && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl flex items-center gap-2" style={{ color: "#0B2D59" }}>
            <Globe className="w-5 h-5" /> Published selection criteria
          </h2>
          <p className="text-sm" style={{ color: "#475569" }}>
            This is the public list applicants see at <code>/criteria</code>. Currently
            <strong> version {pub.version}</strong>. Saving changes publishes a new version; every
            application records the version that was active when it was started and submitted.
          </p>
          <div className="p2p-card p-6 mt-4 space-y-3">
            {pub.criteria.map((item, i) => (
              <textarea key={item.id} rows={2} className="p2p-input" value={item.text}
                data-testid={`admin-public-criteria-${item.id}`}
                onChange={(e) => setPubText(i, e.target.value)} />
            ))}
          </div>
          <button className="p2p-btn-primary mt-4 inline-flex items-center gap-2" onClick={savePub}
            data-testid="admin-public-criteria-save">
            <Save className="w-4 h-4" /> Publish new version
          </button>
        </div>
      )}
      <p className="p2p-help mt-4">
        Reminder: do not score applicants on their personal story. When multiple applicants meet the same requirements,
        select using application completion date, documented housing-readiness criteria, a transparent waiting list,
        or randomized selection among equally qualified applicants.
      </p>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="p2p-label">{label}</span>
      {children}
    </label>
  );
}
